<template>
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
    <div class="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
      <!-- Header -->
      <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
        <h2 class="text-xl font-bold text-gray-900">
          {{ isEdit ? 'Editar Usuário' : 'Novo Usuário' }}
        </h2>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      
      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
          <input
            v-model="form.full_name"
            type="text"
            required
            class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Email (usuário) *</label>
          <input
            v-model="form.email"
            type="email"
            required
            class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
          <input
            v-model="form.phone"
            type="text"
            class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Perfil *</label>
          <select
            v-model="form.role"
            required
            class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="super_admin">Super Administrador</option>
            <option value="company_admin">Administrador de Empresa</option>
            <option value="company_staff">Funcionário de Empresa</option>
          </select>
        </div>
        
        <div v-if="form.role !== 'super_admin'">
          <label class="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
          <select
            v-model="form.company_id"
            class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option :value="null">Selecione uma empresa</option>
            <option v-for="company in companies" :key="company.id" :value="company.id">
              {{ company.name }}
            </option>
          </select>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ isEdit ? 'Nova senha (deixe em branco para manter)' : 'Senha *' }}
          </label>
          <input
            v-model="form.password"
            type="password"
            :required="!isEdit"
            minlength="6"
            class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
        
        <div class="flex items-center gap-2">
          <input
            v-model="form.is_active"
            type="checkbox"
            class="w-4 h-4 text-orange-600 rounded"
          />
          <label class="text-sm text-gray-700">Usuário ativo</label>
        </div>
        
        <!-- Actions -->
        <div class="flex justify-end gap-3 mt-8 pt-4 border-t">
          <button
            type="button"
            @click="$emit('close')"
            class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            :disabled="saving"
            class="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
          >
            {{ saving ? 'Salvando...' : (isEdit ? 'Salvar' : 'Criar Usuário') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { userApi, companyApi } from '@/utils/api'
import { notifyError, notifySuccess } from '@/utils/notifications'
import type { User } from '@/types'

const props = defineProps<{
  user: User | null
  isEdit: boolean
}>()

const emit = defineEmits(['close', 'save'])

const saving = ref(false)
const companies = ref<any[]>([])

const form = ref({
  full_name: '',
  email: '',
  phone: '',
  role: 'company_admin',
  company_id: null as number | null,
  password: '',
  is_active: true
})

// Preenche o formulário se estiver editando
watch(() => props.user, (user) => {
  if (user) {
    form.value = {
      full_name: user.full_name,
      email: user.email,
      phone: user.phone || '',
      role: user.role,
      company_id: user.company_id ?? null,
      password: '',
      is_active: user.is_active
    }
  }
}, { immediate: true })

const loadCompanies = async () => {
  try {
    const res = await companyApi.getAll()
    companies.value = res.data
  } catch (error) {
    console.error('Erro ao carregar empresas:', error)
  }
}

const handleSubmit = async () => {
  // Validação de email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(form.value.email)) {
    notifyError('Email inválido')
    return
  }
  
  // Validação de senha na criação
  if (!props.isEdit && form.value.password.length < 6) {
    notifyError('A senha deve ter pelo menos 6 caracteres')
    return
  }
  
  // Validação de senha na edição (só se preenchida)
  if (props.isEdit && form.value.password && form.value.password.length < 6) {
    notifyError('A senha deve ter pelo menos 6 caracteres')
    return
  }
  
  saving.value = true
  
  try {
    const data: any = {
      full_name: form.value.full_name,
      email: form.value.email,
      phone: form.value.phone || null,
      role: form.value.role,
      is_active: form.value.is_active
    }
    
    if (form.value.role !== 'super_admin') {
      data.company_id = form.value.company_id
    } else {
      data.company_id = null
    }
    
    if (form.value.password) {
      data.password = form.value.password
    }
    
    if (props.isEdit && props.user) {
      await userApi.update(props.user.id, data)
      notifySuccess('Usuário atualizado com sucesso!')
    } else {
      await userApi.create(data)
      notifySuccess('Usuário criado com sucesso!')
    }
    
    emit('save')
  } catch (error: any) {
    notifyError('Erro: ' + (error.response?.data?.detail || 'Erro ao salvar usuário'))
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  loadCompanies()
})
</script>
